/**
 * environment.ts — the active-perception World (paintable truth) and Env (one run).
 *
 * Ported from train/_proto_active_perception.py. The Python prototype is the
 * validated reference (GRU 2.66× random at 20×20); this file is a faithful
 * TypeScript translation of:
 *   - make_clustered_world
 *   - sensor_footprint (trajectory-coupled anisotropic sensor)
 *   - sample_reading (noisy sensor)
 *   - ShadowBelief (Bayesian; used ONLY for shaping-bonus computation)
 *   - build_observation (33-dim candidate-action features)
 *   - Env (one run: pose, sensor, reward accounting, shaping bonus)
 *
 * Everything the agent sees is a function of its belief, never the truth. The
 * truth is what the user paints; the belief is the agent's spatial memory.
 *
 * Reward design (matches the prototype):
 *   +value       entering a gold cell (graded: Beta(2,5) · V_MAX)
 *   -value       stepping onto a stone (graded: Beta(1,5) · K_MAX)
 *   -BUMP_PENALTY walking off the grid (small — exploration is essentially free)
 *   +β·(γ·Φ'−Φ)  potential-based shaping bonus; Φ = coverage(s)
 */

/** True cell classes — the World's ground truth. */
export const FREE = 0;
export const GOLD = 1;
export const STONE = 2;

/** The 8 single-cell directions (matches the prototype's DIRECTIONS).
 *  Cardinals first (E/W/S/N), then diagonals. */
export const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],   // 0 = E
  [0, -1],  // 1 = W
  [1, 0],   // 2 = S
  [-1, 0],  // 3 = N
  [1, 1],   // 4 = SE
  [1, -1],  // 5 = SW
  [-1, 1],  // 6 = NE
  [-1, -1], // 7 = NW
];
export const N_ACTIONS = 8;

/** Grid edge length. Matches the trained model's config (20×20). Changing
 *  this requires retraining. */
export const GRID = 20;
export const START_R = GRID >> 1;
export const START_C = GRID >> 1;

/** Sensor: P(reading == truth). */
export const SENSOR_P = 0.85;
/** Trajectory-coupled sensor reach. Extends `ahead` in the facing direction,
 *  `side` in others. */
export const SENSOR_AHEAD = 5;
export const SENSOR_SIDE = 1;

// Reward-design constants (prototype values).
export const V_MAX = 1.0;
export const K_MAX = 0.2;
export const BUMP_PENALTY = 0.02;
export const MAX_STEPS = 160;
export const GAMMA = 0.90;
export const SHAPING_BETA = 0.3;

/** Default layout generator parameters. */
export const N_CLUSTERS = 12;
export const CLUSTER_SPREAD = 1;
export const GOLD_P = 0.6;
export const STONE_P = 0.06;

// ─── World (the paintable ground truth) ──────────────────────────────────────

export class World {
  readonly grid: number;
  readonly cells: Uint8Array;
  /** Per-cell value in [0, V_MAX] for gold, [0, K_MAX] for stone. The agent
   *  never sees this; it learns from observed rewards. */
  readonly values: Float64Array;
  private goldValueTotal = 0;

  constructor(grid: number) {
    this.grid = grid;
    this.cells = new Uint8Array(grid * grid);
    this.values = new Float64Array(grid * grid);
  }

  inBounds(r: number, c: number): boolean {
    return r >= 0 && r < this.grid && c >= 0 && c < this.grid;
  }

  /** Paint a cell. Respects mutual exclusion (FREE erases). Keeps
   *  goldValueTotal consistent so the env's done-check stays correct under
   *  live edits. Default value for newly-painted gold is V_MAX. */
  paint(r: number, c: number, kind: number, value?: number): void {
    if (!this.inBounds(r, c)) return;
    const i = r * this.grid + c;
    const prev = this.cells[i];
    if (prev === GOLD) this.goldValueTotal -= this.values[i];
    this.cells[i] = kind;
    if (kind === GOLD) {
      this.values[i] = value ?? V_MAX;
      this.goldValueTotal += this.values[i];
    } else {
      this.values[i] = kind === STONE ? (value ?? K_MAX * 0.5) : 0;
    }
  }

  /** Bulk-load cells + values from a generator. Resets goldValueTotal. */
  loadFrom(cells: ArrayLike<number>, values?: ArrayLike<number>): void {
    this.goldValueTotal = 0;
    const n = this.cells.length;
    for (let i = 0; i < n; i++) {
      this.cells[i] = cells[i] ?? 0;
      this.values[i] = values ? values[i] ?? 0 : 0;
      if (this.cells[i] === GOLD) this.goldValueTotal += this.values[i];
    }
  }

  /** Total remaining gold value — drives the env's done check. */
  get totalGoldValue(): number { return this.goldValueTotal; }
}

// ─── Clustered-world generator (matches make_clustered_world) ─────────────────

export interface WorldGenOptions {
  nClusters?: number;
  clusterSpread?: number;
  goldP?: number;
  stoneP?: number;
  rng?: () => number;
}

/** Mulberry32 PRNG. Same as linalg.ts:makeRng — duplicated here to keep the
 *  env module standalone (the World generator needs an rng with the same
 *  semantics as numpy.random.default_rng for reproducibility across the
 *  Python training and the TS demo). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Sample from a Beta(α, β) distribution via the gamma-ratio method.
 *  Matches numpy.random.beta's algorithm well enough for layout generation. */
function sampleBeta(rng: () => number, a: number, b: number): number {
  const x = sampleGamma(rng, a);
  const y = sampleGamma(rng, b);
  return x / (x + y);
}

function sampleGamma(rng: () => number, shape: number): number {
  // Marsaglia–Tsang for shape ≥ 1; for shape < 1, scale up then down.
  if (shape < 1) {
    const u = rng();
    return sampleGamma(rng, shape + 1) * Math.pow(u, 1 / shape);
  }
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  while (true) {
    let x: number, v: number;
    do {
      x = sampleStandardNormal(rng);
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = rng();
    if (u < 1 - 0.0331 * x * x * x * x) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

function sampleStandardNormal(rng: () => number): number {
  let u = 0, v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Generate a clustered world. Mirrors the prototype's make_clustered_world.
 *  Gold appears in tight clusters with Beta(2,5)-graded values; stones are
 *  scattered (avoiding gold) with Beta(1,5)-graded costs. A 3×3 region around
 *  the start is forced FREE so the agent isn't born on a stone. */
export function makeClusteredWorld(
  grid: number,
  seed: number,
  opts: WorldGenOptions = {},
): { cells: Uint8Array; values: Float64Array } {
  const nClusters = opts.nClusters ?? N_CLUSTERS;
  const spread = opts.clusterSpread ?? CLUSTER_SPREAD;
  const goldP = opts.goldP ?? GOLD_P;
  const stoneP = opts.stoneP ?? STONE_P;
  const rng = opts.rng ?? mulberry32(seed);

  const cells = new Uint8Array(grid * grid); // all FREE
  const values = new Float64Array(grid * grid);

  // Cluster centers — sampled in [2, grid-2)² like the prototype.
  const centers: Array<[number, number]> = [];
  for (let k = 0; k < nClusters; k++) {
    const cr = 2 + Math.floor(rng() * (grid - 4));
    const cc = 2 + Math.floor(rng() * (grid - 4));
    centers.push([cr, cc]);
  }
  for (const [cr, cc] of centers) {
    for (let dr = -spread; dr <= spread; dr++) {
      for (let dc = -spread; dc <= spread; dc++) {
        const nr = cr + dr;
        const nc = cc + dc;
        if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
        if (rng() < goldP) {
          cells[nr * grid + nc] = GOLD;
          values[nr * grid + nc] = sampleBeta(rng, 2, 5) * V_MAX;
        }
      }
    }
  }
  // Scattered stones, avoiding gold cells. Beta(1,5) → mostly small costs.
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] !== FREE) continue;
    if (rng() < stoneP) {
      cells[i] = STONE;
      values[i] = sampleBeta(rng, 1, 5) * K_MAX;
    }
  }
  // Force the start region FREE.
  const startR = grid >> 1;
  const startC = grid >> 1;
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const r = startR + dr;
      const c = startC + dc;
      if (r < 0 || r >= grid || c < 0 || c >= grid) continue;
      cells[r * grid + c] = FREE;
      values[r * grid + c] = 0;
    }
  }
  return { cells, values };
}

// ─── Trajectory-coupled sensor footprint ─────────────────────────────────────

export interface SensorCell { r: number; c: number; dr: number; dc: number; }

/** Compute the sensor footprint at (r, c) given the current facing direction.
 *
 * - If `facing` is null (agent hasn't moved yet): a small (2·side+1)² disc.
 * - Otherwise: cells within `ahead` of the facing direction, `side` of others.
 *   This is the regime's defining affordance — the agent sees further along
 *   its trajectory than to the sides.
 */
export function sensorFootprint(
  r: number, c: number,
  facing: readonly [number, number] | null,
  grid: number,
  ahead: number = SENSOR_AHEAD,
  side: number = SENSOR_SIDE,
): SensorCell[] {
  const out: SensorCell[] = [];
  if (facing === null) {
    for (let dr = -side; dr <= side; dr++) {
      for (let dc = -side; dc <= side; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
        out.push({ r: nr, c: nc, dr, dc });
      }
    }
    return out;
  }
  const [fr, fc] = facing;
  for (let dr = -ahead; dr <= ahead; dr++) {
    for (let dc = -ahead; dc <= ahead; dc++) {
      if (dr === 0 && dc === 0) continue;
      const dot = dr * fr + dc * fc;
      const reach = dot > 0 ? ahead : side;
      if (dr * dr + dc * dc <= reach * reach) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
        out.push({ r: nr, c: nc, dr, dc });
      }
    }
  }
  return out;
}

/** Sample one noisy sensor reading for a cell whose true class is `truth`.
 *  With probability SENSOR_P the reading equals truth; otherwise uniformly
 *  random over the other two classes. */
export function sampleReading(truth: number, rng: () => number): number {
  if (rng() < SENSOR_P) return truth;
  const wrong = truth === FREE ? [GOLD, STONE] : truth === GOLD ? [FREE, STONE] : [FREE, GOLD];
  return wrong[Math.floor(rng() * 2)];
}

// ─── Shadow belief map (Bayesian; used ONLY for shaping bonus) ────────────────

/** Bayesian belief over the grid — used ONLY for the potential-based shaping
 *  bonus Φ(s) = coverage(s). The agent never sees this map directly; the
 *  observation (build_observation) reads from it because it has no other
 *  source of "what's where", but the bonus is computed separately and is
 *  policy-invariant (Ng, Harada & Russell 1999).
 *
 *  Mirrors the prototype's ShadowBelief:
 *    - bGold, bStone: per-cell beliefs in [0, 1]
 *    - confident[i]: true once the agent has visited i (certain FREE)
 *    - bilateral update: GOLD pulls bStone→0, STONE pulls bGold→0, FREE pulls both→0
 *    - coverage: fraction of cells with bGold>0.7 or bStone>0.7 or confident
 */
export class ShadowBelief {
  readonly grid: number;
  readonly bGold: Float64Array;
  readonly bStone: Float64Array;
  readonly confident: Uint8Array;

  constructor(grid: number) {
    this.grid = grid;
    this.bGold = new Float64Array(grid * grid);
    this.bStone = new Float64Array(grid * grid);
    this.confident = new Uint8Array(grid * grid);
  }

  reset(priorGold: number): void {
    this.bGold.fill(priorGold);
    this.bStone.fill(0.1);
    this.confident.fill(0);
  }

  update(r: number, c: number, reading: number, lr = 0.5): void {
    const i = r * this.grid + c;
    if (this.confident[i]) return;
    const d = 1 - lr;
    if (reading === GOLD) {
      this.bGold[i] = d * this.bGold[i] + lr;
      this.bStone[i] = d * this.bStone[i];
    } else if (reading === STONE) {
      this.bStone[i] = d * this.bStone[i] + lr;
      this.bGold[i] = d * this.bGold[i];
    } else { // FREE
      this.bGold[i] = d * this.bGold[i];
      this.bStone[i] = d * this.bStone[i];
    }
  }

  markVisited(r: number, c: number): void {
    const i = r * this.grid + c;
    this.bGold[i] = 0;
    this.bStone[i] = 0;
    this.confident[i] = 1;
  }

  coverage(): number {
    let n = 0;
    for (let i = 0; i < this.bGold.length; i++) {
      if (this.bGold[i] > 0.7 || this.bStone[i] > 0.7 || this.confident[i]) n++;
    }
    return n / this.bGold.length;
  }
}

// ─── Observation (33-dim candidate-action features) ───────────────────────────

export const D_OBS = 33; // 8 actions × 4 features + 1 (prev_reward)

/** Build the 33-dim observation the GRU sees. Matches the prototype's
 *  build_observation:
 *    feats[a, 0..3] = [dest_b_gold, dest_b_stone, can_move, const]
 *    + prev_reward scalar
 *
 *  For each candidate direction a:
 *    dest_b_gold  — shadow belief that the destination cell has gold
 *    dest_b_stone — shadow belief that the destination is a stone
 *    can_move     — 1 if the destination is in-bounds AND not certainly a stone
 *    const        — always 1 (lets the network learn an action-specific bias)
 */
export function buildObservation(
  belief: ShadowBelief,
  cells: Uint8Array,
  r: number, c: number,
  prevReward: number,
  grid: number,
  out?: Float64Array,
): Float64Array {
  const buf = out ?? new Float64Array(D_OBS);
  let off = 0;
  for (let a = 0; a < N_ACTIONS; a++) {
    const [dr, dc] = DIRECTIONS[a];
    const nr = r + dr;
    const nc = c + dc;
    if (nr >= 0 && nr < grid && nc >= 0 && nc < grid) {
      const idx = nr * grid + nc;
      buf[off + 0] = belief.bGold[idx];
      buf[off + 1] = belief.bStone[idx];
      buf[off + 2] = cells[idx] === STONE ? 0 : 1;
    } else {
      buf[off + 0] = 0;
      buf[off + 1] = 1; // mark OOB as believed-stone so the agent avoids it
      buf[off + 2] = 0;
    }
    buf[off + 3] = 1;
    off += 4;
  }
  buf[32] = prevReward;
  return buf;
}

// ─── Env (one run's state) ───────────────────────────────────────────────────

export type StepEvent = 'gold' | 'stone' | 'bump' | 'step';

export interface StepOutcome {
  reward: number;
  done: boolean;
  event: StepEvent;
  /** Post-step pose. */
  r: number;
  c: number;
  /** Post-step observation (33-dim). */
  obs: Float64Array;
}

export class Env {
  readonly shadow: ShadowBelief;
  r = START_R;
  c = START_C;
  facing: readonly [number, number] | null = null;
  steps = 0;
  done = false;
  /** Gold value remaining on the grid (drives the done check). */
  valueLeft = 0;
  /** Total gold value collected this episode. */
  totalCollected = 0;
  /** Reward of the most recent step (input to the next observation). */
  prevReward = 0;
  /** Sensor footprint at the current pose — exposed for the renderer. */
  footprint: SensorCell[] = [];

  /** Reset snapshot — the env can be reset to this state cheaply. */
  private initialCells: Uint8Array;
  private initialValues: Float64Array;

  constructor(
    readonly world: World,
    readonly rng: () => number,
  ) {
    this.shadow = new ShadowBelief(world.grid);
    this.initialCells = world.cells.slice();
    this.initialValues = world.values.slice();
    this._reset();
  }

  /** Snapshot the world's current state as the new initial layout. Call after
   *  the user paints to make their edits persist across resets within a run. */
  snapshotWorld(): void {
    this.initialCells = this.world.cells.slice();
    this.initialValues = this.world.values.slice();
  }

  private _reset(): void {
    // Restore the world to its snapshot.
    this.world.cells.set(this.initialCells);
    this.world.values.set(this.initialValues);

    this.r = START_R;
    this.c = START_C;
    this.facing = null;
    this.steps = 0;
    this.done = false;
    this.totalCollected = 0;
    this.prevReward = 0;

    // Recompute valueLeft.
    let vl = 0;
    for (let i = 0; i < this.world.cells.length; i++) {
      if (this.world.cells[i] === GOLD) vl += this.world.values[i];
    }
    this.valueLeft = vl;

    // Reset the shadow belief to a uniform prior and seed it at the start.
    const priorGold = this.world.cells.filter((c) => c === GOLD).length / this.world.cells.length;
    this.shadow.reset(priorGold);
    this.shadow.markVisited(this.r, this.c);
    this._sense();
  }

  /** Re-sense at the current pose. Updates the shadow belief from noisy
   *  readings of the trajectory-coupled footprint. */
  private _sense(): void {
    this.footprint = sensorFootprint(this.r, this.c, this.facing, this.world.grid);
    for (const { r, c } of this.footprint) {
      const truth = this.world.cells[r * this.world.grid + c];
      const reading = sampleReading(truth, this.rng);
      this.shadow.update(r, c, reading);
    }
  }

  /** Build the current observation. */
  obs(): Float64Array {
    return buildObservation(
      this.shadow, this.world.cells, this.r, this.c, this.prevReward, this.world.grid);
  }

  /** Reset to the snapshot. Returns the first observation. */
  reset(): Float64Array {
    this._reset();
    return this.obs();
  }

  /** Re-sync valueLeft from the world's current gold-value total. Call after
   *  the user paints gold/free mid-run so the done-check stays honest. */
  resyncValueLeft(): void {
    let vl = 0;
    for (let i = 0; i < this.world.cells.length; i++) {
      if (this.world.cells[i] === GOLD) vl += this.world.values[i];
    }
    this.valueLeft = vl;
  }

  /** Execute one single-cell move. Mirrors the prototype's Env.step. */
  step(action: number): StepOutcome {
    if (this.done) {
      return { reward: 0, done: true, event: 'step', r: this.r, c: this.c, obs: this.obs() };
    }
    const [dr, dc] = DIRECTIONS[action];
    const nr = this.r + dr;
    const nc = this.c + dc;
    const grid = this.world.grid;
    let reward = 0;
    let event: StepEvent = 'step';

    const blocked = nr < 0 || nr >= grid || nc < 0 || nc >= grid;
    if (!blocked && this.world.cells[nr * grid + nc] === STONE) {
      // Stepping onto stone: pay graded cost, push through.
      reward -= this.world.values[nr * grid + nc];
      this.world.values[nr * grid + nc] = 0; // cost paid only once
      event = 'stone';
    } else if (blocked) {
      reward -= BUMP_PENALTY;
      event = 'bump';
    }

    let endR = this.r;
    let endC = this.c;
    if (!blocked) {
      endR = nr;
      endC = nc;
      const idx = nr * grid + nc;
      if (this.world.cells[idx] === GOLD) {
        reward += this.world.values[idx];
        this.totalCollected += this.world.values[idx];
        this.valueLeft -= this.world.values[idx];
        this.world.cells[idx] = FREE;
        this.world.values[idx] = 0;
        event = 'gold';
      }
    }

    // Potential-based shaping bonus (Ng, Harada & Russell 1999).
    const phiBefore = this.shadow.coverage();
    this.r = endR;
    this.c = endC;
    this.facing = [dr, dc];
    this.shadow.markVisited(this.r, this.c);
    this.steps++;
    this._sense();
    const phiAfter = this.shadow.coverage();
    reward += SHAPING_BETA * (GAMMA * phiAfter - phiBefore);

    this.prevReward = reward;
    if (this.valueLeft <= 0.01 || this.steps >= MAX_STEPS) this.done = true;

    return { reward, done: this.done, event, r: this.r, c: this.c, obs: this.obs() };
  }
}
